import { useEffect, useState } from "react";
import { ChevronDown, Package, Undo2, AlertTriangle, Truck, Copy, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice, imgUrl, formatApiError } from "../../lib/api";
import { WoltBox, WOLT_AZ, PREF_AZ } from "./WoltBox";

const STATUSES = ["new", "processing", "shipped", "completed", "cancelled"];
const STATUS_AZ = { new: "Yeni", processing: "Hazırlanır", shipped: "Göndərildi", completed: "Tamamlandı", cancelled: "Ləğv edildi" };

// Ödəniş vəziyyəti. "review" — imza düzgündür, amma məbləğ təsdiqlənməyib;
// mal göndərilməzdən əvvəl epoint kabinetindən yoxlanmalıdır.
const PAY_AZ = {
  pending: "gözləyir",
  paid: "ödənilib",
  failed: "ödənilməyib",
  refunded: "geri qaytarılıb",
  partially_refunded: "qismən qaytarılıb",
  // Müştəri ödənişi yarımçıq qoyub — sifariş özü ləğv olunub, stok qayıdıb.
  expired: "vaxtı keçib",
  review: "YOXLANMALIDIR",
};

// Kartı açmadan da neçə məhsul olduğu görünsün.
const itemCount = (o) =>
  (o.items || []).reduce((s, it) => s + (it.quantity || 1), 0);


const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [openId, setOpenId] = useState(null);
  // Pul qaytarma iki addımlıdır: əvvəl pul, sonra "məhsul satışa qayıtsın?"
  const [refund, setRefund] = useState(null);   // {order, amount, reason}
  const [busy, setBusy] = useState(false);
  const [askStock, setAskStock] = useState(null); // {order}

  const load = () => api.get("/admin/orders").then(({ data }) => setOrders(data));
  useEffect(() => { load(); }, []);

  // Wolt açarları qurulubmu — qurulmayıbsa panel köhnə kimi qalır.
  const [woltEnabled, setWoltEnabled] = useState(false);
  useEffect(() => {
    api.get("/config").then(({ data }) => setWoltEnabled(!!data.wolt_enabled)).catch(() => {});
  }, []);
  const showWolt = (o) =>
    woltEnabled && o.payment_status === "paid" && o.delivery_method !== "pickup";

  // Çatdırılma açıq/bağlı (təsisçi qərarı, 2026-10-20). Yalnız müştərinin
  // gördüyü vəd dəyişir — sifariş qəbulu və «Kuryer çağır» eyni qalır.
  const [deliveryOnline, setDeliveryOnline] = useState(true);
  useEffect(() => {
    if (!woltEnabled) return;
    api.get("/admin/settings/delivery")
      .then(({ data }) => setDeliveryOnline(data.online !== false))
      .catch(() => {});
  }, [woltEnabled]);
  const toggleDelivery = async () => {
    try {
      const { data } = await api.put("/admin/settings/delivery", { online: !deliveryOnline });
      setDeliveryOnline(data.online);
      toast.success(data.online
        ? "Çatdırılma açıldı"
        : "Çatdırılma bağlandı — müştəri «sizinlə əlaqə saxlanılacaq» görür");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remaining = (o) =>
    Math.max(0, Math.round(((o.total || 0) - (o.refunded_amount || 0)) * 100) / 100);

  const doRefund = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${refund.order.id}/refund`, {
        amount: Number(String(refund.amount).replace(",", ".")),
        reason: refund.reason,
      });
      const done = refund.order;
      setRefund(null);
      toast.success(`${data.refunded_amount} ₼ müştəriyə qaytarıldı`);
      await load();
      // Pul qayıtdı — indi malın taleyini soruşuruq.
      if (data.stock_restorable) setAskStock({ order: done });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const restoreStock = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${askStock.order.id}/restore-stock`);
      toast.success("Məhsullar yenidən satışdadır");
      setAskStock(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // --- Çatdırılma haqqı ---
  const [delivery, setDelivery] = useState({});   // {orderId: "4.50"}

  /**
   * Müştəriyə göndəriləcək ödəniş linki.
   *
   * Sifarişin öz açarından qurulur — əvvəl link yalnız yaradıldığı anda
   * yaddaşda saxlanılırdı və səhifə yenilənəndən sonra yoxa çıxırdı.
   */
  const deliveryLink = (o) =>
    o.delivery?.token
      ? `${window.location.origin}/delivery/${o.order_number}?token=${o.delivery.token}`
      : "";

  const saveDelivery = async (o) => {
    const amount = Number(String(delivery[o.id] ?? "").replace(",", "."));
    if (!amount || amount <= 0) {
      toast.error("Məbləği yazın");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/orders/${o.id}/delivery`, { amount });
      toast.success("Ödəniş linki hazırdır");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copyDeliveryLink = async (o) => {
    const url = deliveryLink(o);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link kopyalandı");
    } catch {
      window.prompt("Linki kopyalayın:", url);
    }
  };

  /** Müştəriyə birbaşa WhatsApp-da göndərmək — nömrə əl ilə yazılmır. */
  const sendDeliveryLink = (o) => {
    const digits = String(o.customer?.phone || "").replace(/\D/g, "");
    let wa = digits;
    if (digits.length === 9) wa = "994" + digits;
    else if (digits.length === 10 && digits.startsWith("0")) wa = "994" + digits.slice(1);
    const text = encodeURIComponent(
      `Salam! Sparkle — ${o.order_number} nömrəli sifarişiniz üçün çatdırılma haqqı: ` +
      `${formatPrice(o.delivery?.amount || 0)}.\nÖdəniş linki: ${deliveryLink(o)}`);
    window.open(`https://wa.me/${wa}?text=${text}`, "_blank", "noopener");
  };

  const changeStatus = async (orderId, status) => {
    try {
      await api.put(`/admin/orders/${orderId}/status`, { status });
      toast.success("Status yeniləndi");
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status } : o)));
    } catch {
      toast.error("Yeniləmə uğursuz oldu");
    }
  };

  return (
    <div data-testid="admin-orders">
      <h1 className="font-serif font-light text-3xl sm:text-4xl tracking-tight mb-6 sm:mb-8">Sifarişlər</h1>
      {woltEnabled && (
        <div className="mb-6 flex flex-wrap items-center gap-3 border border-brand-line rounded-md bg-white px-4 py-3"
             data-testid="delivery-switch">
          <Truck size={16} strokeWidth={1.5} />
          <span className="text-sm">Çatdırılma qəbulu</span>
          <button type="button" onClick={toggleDelivery} data-testid="delivery-switch-btn"
            className={`px-3 py-1.5 text-xs rounded-md border ${deliveryOnline ? "bg-brand-ink text-white border-brand-ink" : "border-destructive text-destructive"}`}>
            {deliveryOnline ? "Açıq" : "Bağlı"}
          </button>
          <span className="text-xs text-brand-muted">
            {deliveryOnline
              ? "Müştəri adi qaydada sifariş verir; kuryeri siz «Kuryer çağır» ilə göndərirsiniz."
              : "Sifariş yenə qəbul olunur, amma müştəri görür: «Hazırda göndərilə bilmir — dəqiq vaxtı təyin etmək üçün sizinlə əlaqə saxlanılacaq»."}
          </span>
        </div>
      )}
      <div className="space-y-4">
        {orders.map((o) => (
          <div key={o.id} className="bg-white border border-brand-line rounded-md" data-testid={`admin-order-${o.order_number}`}>
            <div className="p-4 sm:p-5 grid grid-cols-2 lg:flex lg:flex-wrap lg:items-center lg:justify-between gap-3 sm:gap-4">
              <div>
                <p className="font-mono text-sm">
                  {o.order_number}
                  {/* Bağlamanın üstünə yazılan qısa nömrə (kuryer bununla tanıyır). */}
                  {o.seq && o.delivery_method === "wolt" && (
                    <span className="ml-2 text-brand-sub" title="Bağlama nömrəsi — qutunun üstünə yazın"
                          data-testid={`seq-${o.order_number}`}>№ {o.seq}</span>
                  )}
                </p>
                <p className="text-xs text-brand-muted mt-1">{new Date(o.created_at).toLocaleString()}</p>
              </div>
              <div className="text-sm">
                <p className="font-medium">{o.customer?.name}</p>
                <p className="text-brand-muted text-xs">{o.customer?.phone}</p>
              </div>
              <p className={`text-xs sm:text-sm col-span-2 lg:col-span-1 ${o.payment_status === "review" ? "text-destructive font-medium" : "text-brand-muted"}`}>
                {o.payment_method === "cod" ? "Çatdırılmada" : "Epoint"} · {PAY_AZ[o.payment_status] || o.payment_status}
                {o.delivery_method === "pickup" && " · 🏬 götürəcək"}
                {o.delivery_method === "wolt" && o.payment_status === "paid" && (
                  <span data-testid={`wolt-badge-${o.order_number}`}>
                    {" · 🚚 "}{WOLT_AZ[o.wolt?.status] || "Wolt"}
                  </span>
                )}
                {/* Kartı açmadan görünməli olanlar: vaxt istəyi və hədiyyə. */}
                {o.delivery_pref && o.delivery_pref !== "asap" && (
                  <span data-testid={`pref-badge-${o.order_number}`}>
                    {" · ⏰ "}{PREF_AZ[o.delivery_pref] || o.delivery_pref}
                  </span>
                )}
                {o.recipient?.phone && <span data-testid={`gift-badge-${o.order_number}`}> · 🎁 hədiyyə</span>}
              </p>
              <p className="font-serif text-lg sm:text-xl text-right lg:text-left">{formatPrice(o.total)}</p>
              <select
                value={o.status}
                onChange={(e) => changeStatus(o.id, e.target.value)}
                data-testid={`order-status-${o.order_number}`}
                className="col-span-2 lg:col-span-1 w-full lg:w-auto border border-brand-line rounded-md px-3 py-2.5 text-sm bg-white"
              >
                {STATUSES.map((s) => <option key={s} value={s}>{STATUS_AZ[s]}</option>)}
              </select>
            </div>

            {/* Açma düyməsi AYRICA və görünəndir.
                Əvvəl bütün kart gizli şəkildə basılan idi — sahibkar kartın
                açıldığını bilmirdi və status siyahısına toxunub heç nə
                baş vermədiyini düşünürdü. */}
            <button
              type="button"
              onClick={() => setOpenId(openId === o.id ? null : o.id)}
              data-testid={`order-toggle-${o.order_number}`}
              aria-expanded={openId === o.id}
              className="w-full flex items-center justify-center gap-2 border-t border-brand-line px-4 py-3.5 text-sm text-brand-sub hover:bg-brand-bg2 transition-colors"
            >
              <Package size={16} strokeWidth={1.5} />
              {openId === o.id
                ? "Bağla"
                : `Nə sifariş edilib? · ${itemCount(o)} məhsul`}
              <ChevronDown
                size={16}
                strokeWidth={1.5}
                className={`transition-transform ${openId === o.id ? "rotate-180" : ""}`}
              />
            </button>
            {openId === o.id && (
              <div className="border-t border-brand-line p-4 sm:p-5 bg-brand-bg2/50 text-sm">
                <p className="overline mb-3">Məhsullar</p>
                <div className="space-y-3 mb-5">
                  {o.items.map((it, i) => (
                    <div key={i} className="flex items-start gap-4">
                      {/* Sifariş anındakı şəkil — anbardan yığmaq üçün.
                          Telefonda daha böyük: məhsul şəkildən tanınmalıdır. */}
                      <a
                        href={`/product/${it.product_id}`}
                        target="_blank"
                        rel="noreferrer"
                        title="Məhsulun səhifəsini aç (bütün şəkillər)"
                        className="shrink-0 block w-24 sm:w-20 aspect-[4/5] overflow-hidden rounded-md bg-brand-bg2 border border-brand-line"
                      >
                        <img
                          src={imgUrl(it.image)}
                          alt={it.name}
                          loading="lazy"
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </a>

                      <div className="flex-1 min-w-0 flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-medium truncate">{it.name}</p>
                          <p className="text-xs text-brand-muted mt-0.5">
                            {it.code ? `${it.code} · ` : ""}×{it.quantity}
                            {it.size ? ` · ${it.size}` : ""}
                            {it.color ? ` · ${it.color}` : ""}
                          </p>
                          <p className="text-xs text-brand-muted">
                            {formatPrice(it.price)} / ədəd
                          </p>
                        </div>
                        <span className="font-serif text-lg shrink-0">
                          {formatPrice(it.price * it.quantity)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                {o.discount_amount > 0 && (
                  /* Endirim kartı tətbiq olunub — sahibkar məbləğin niyə
                     az olduğunu görsün. */
                  <div className="mb-5 border border-brand-line rounded-md p-3 bg-white space-y-1">
                    <div className="flex justify-between">
                      <span className="text-brand-muted">Ara cəm</span>
                      <span>{formatPrice(o.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-brand-oxid">
                      <span>
                        Endirim kartı · {o.discount_percent}%
                        {o.discount_card ? ` · ${o.discount_card}` : ""}
                      </span>
                      <span>−{formatPrice(o.discount_amount)}</span>
                    </div>
                    <div className="flex justify-between font-medium border-t border-brand-line pt-1">
                      <span>Ödəniləcək</span>
                      <span>{formatPrice(o.total)}</span>
                    </div>
                  </div>
                )}
                {o.refunded_amount > 0 && (
                  <div className="mb-5 border border-destructive/40 rounded-md p-3 bg-white text-destructive">
                    <p className="font-medium">
                      Geri qaytarılıb: {formatPrice(o.refunded_amount)}
                      {remaining(o) > 0 && ` (qalıq ${formatPrice(remaining(o))})`}
                    </p>
                    {o.refund_reason && (
                      <p className="text-xs mt-1 text-brand-muted">Səbəb: {o.refund_reason}</p>
                    )}
                    {o.stock_restored && (
                      <p className="text-xs mt-1 text-brand-muted">Məhsullar satışa qaytarılıb.</p>
                    )}
                  </div>
                )}
                <p className="overline mb-1">{o.delivery_method === "pickup" ? "Çatdırılma" : "Ünvan"}</p>
                {/* Wolt açıqdırsa ünvan və kuryer WoltBox-dadır (ünvanı orada
                    düzəltmək olur). Köhnə axında ünvan telefonla alınır. */}
                {o.delivery_method === "pickup" ? (
                  <p className="text-brand-sub">🏬 Müştəri mağazadan götürəcək</p>
                ) : showWolt(o) ? (
                  <WoltBox order={o} onChanged={load} />
                ) : [o.address?.city, o.address?.line].filter(Boolean).length > 0 ? (
                  <div className="text-brand-sub space-y-0.5">
                    <p>
                      {[o.address?.city, o.address?.line].filter(Boolean).join(", ")}
                      {o.address?.flat ? ` · ${o.address.flat}` : ""}
                    </p>
                    {o.address?.note && <p className="text-xs">📝 {o.address.note}</p>}
                    {o.recipient?.phone && (
                      <p className="text-xs">🎁 Alıcı: {o.recipient.name} · {o.recipient.phone}</p>
                    )}
                    {o.delivery_pref && o.delivery_pref !== "asap" && (
                      <p className="text-xs">⏰ İstək: {PREF_AZ[o.delivery_pref] || o.delivery_pref}</p>
                    )}
                  </div>
                ) : (
                  <p className="text-brand-muted italic">
                    Ünvan yoxdur — müştəri ilə telefonla dəqiqləşdirin.
                  </p>
                )}

                {/* ÇATDIRILMA HAQQI.
                    Wolt kuryerin qiyməti məsafəyə görə dəyişir, ona görə
                    əvvəlcədən bilinmir. Sahibkar qiyməti öyrənəndən sonra
                    burada yazır, sistem müştəri üçün ödəniş linki qurur. */}
                {o.payment_status === "paid" && !o.delivery_method && (
                  <div className="mt-5 border border-brand-line rounded-md p-4 bg-white"
                       data-testid={`delivery-box-${o.order_number}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <Truck size={16} strokeWidth={1.5} />
                      <span className="overline">Çatdırılma haqqı</span>
                      {o.delivery?.status === "paid" && (
                        <span className="text-xs text-brand-oxid ml-auto">
                          ödənilib · {formatPrice(o.delivery.amount)}
                        </span>
                      )}
                    </div>

                    {o.delivery?.status === "paid" ? (
                      <p className="text-xs text-brand-muted">
                        Müştəri ödəyib, kuryeri göndərə bilərsiniz.
                      </p>
                    ) : (
                      <>
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="Wolt qiyməti"
                            value={delivery[o.id] ?? (o.delivery?.amount ?? "")}
                            onChange={(e) => setDelivery((p) => ({ ...p, [o.id]: e.target.value }))}
                            data-testid={`delivery-amount-${o.order_number}`}
                            className="w-32 border border-brand-line rounded-md px-3 py-2.5 text-sm outline-none focus:border-brand-ink"
                          />
                          <span className="text-sm text-brand-muted">₼</span>
                          <button
                            type="button"
                            onClick={() => saveDelivery(o)}
                            disabled={busy}
                            data-testid={`delivery-save-${o.order_number}`}
                            className="bg-brand-ink text-white px-4 py-2.5 text-sm rounded-md disabled:opacity-50"
                          >
                            {o.delivery ? "Yenilə" : "Link yarat"}
                          </button>
                        </div>

                        {deliveryLink(o) && (
                          <div className="mt-3 space-y-2">
                            <p className="text-xs text-brand-muted break-all font-mono bg-brand-bg2 p-2 rounded">
                              {deliveryLink(o)}
                            </p>
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => copyDeliveryLink(o)}
                                data-testid={`delivery-copy-${o.order_number}`}
                                className="flex items-center gap-2 border border-brand-line px-3 py-2 text-sm rounded-md hover:bg-brand-bg2"
                              >
                                <Copy size={14} strokeWidth={1.5} /> Linki kopyala
                              </button>
                              <button
                                type="button"
                                onClick={() => sendDeliveryLink(o)}
                                data-testid={`delivery-wa-${o.order_number}`}
                                className="flex items-center gap-2 border border-brand-line px-3 py-2 text-sm rounded-md hover:bg-brand-bg2"
                              >
                                <Send size={14} strokeWidth={1.5} /> WhatsApp-da göndər
                              </button>
                            </div>
                          </div>
                        )}

                        {o.delivery && (
                          <p className="mt-3 text-xs text-brand-muted">
                            Müştəri hələ ödəməyib. Qiymət dəyişsə məbləği yazıb
                            “Yenilə” basın — link eyni qalır.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Pul qaytarma yalnız ödənilmiş sifaris üçün görünür. */}
                {["paid", "partially_refunded"].includes(o.payment_status) && remaining(o) > 0 && (
                  <button
                    type="button"
                    onClick={() => setRefund({ order: o, amount: remaining(o), reason: "" })}
                    data-testid={`refund-btn-${o.order_number}`}
                    className="mt-5 flex items-center gap-2 border border-destructive text-destructive px-4 py-2.5 text-sm rounded-md hover:bg-destructive hover:text-white transition-colors"
                  >
                    <Undo2 size={16} strokeWidth={1.5} /> Ödənişi qaytar
                  </button>
                )}
                {o.payment_status === "refunded" && !o.stock_restored && (
                  <button
                    type="button"
                    onClick={() => setAskStock({ order: o })}
                    data-testid={`restore-btn-${o.order_number}`}
                    className="mt-5 flex items-center gap-2 border border-brand-line px-4 py-2.5 text-sm rounded-md hover:bg-brand-bg2"
                  >
                    <Package size={16} strokeWidth={1.5} /> Məhsulu satışa qaytar
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
        {orders.length === 0 && <p className="text-brand-muted py-10 text-center">Sifariş yoxdur</p>}
      </div>

      {/* 1-ci addım: pulun qaytarılması. REAL PUL — ona görə təsdiq istənilir. */}
      {refund && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
             data-testid="refund-modal">
          <div className="bg-white w-full max-w-md rounded-md">
            <div className="flex items-center gap-3 border-b border-brand-line px-5 py-4">
              <AlertTriangle size={20} strokeWidth={1.5} className="text-destructive" />
              <p className="font-medium">Ödənişi geri qaytar</p>
            </div>
            <div className="p-5 space-y-4 text-sm">
              <p className="text-brand-sub">
                <span className="font-mono">{refund.order.order_number}</span> —{" "}
                {refund.order.customer?.name} ({refund.order.customer?.phone})
              </p>
              <div>
                <label className="overline block mb-2">Qaytarılacaq məbləğ (₼)</label>
                <input
                  value={refund.amount}
                  onChange={(e) => setRefund({ ...refund, amount: e.target.value })}
                  data-testid="refund-amount"
                  className="w-full border border-brand-line rounded-md px-3 py-2.5 text-sm"
                />
                <p className="text-xs text-brand-muted mt-1">
                  Qaytarıla bilən qalıq: {formatPrice(remaining(refund.order))}.
                  Az yazsanız hissəvi qaytarma olur.
                </p>
              </div>
              <div>
                <label className="overline block mb-2">Səbəb (istəyə bağlı)</label>
                <input
                  value={refund.reason}
                  onChange={(e) => setRefund({ ...refund, reason: e.target.value })}
                  placeholder="məs. məhsul zədələnib"
                  data-testid="refund-reason"
                  className="w-full border border-brand-line rounded-md px-3 py-2.5 text-sm"
                />
              </div>
              <p className="text-xs text-destructive">
                Pul müştərinin kartına qaytarılacaq. Bu əməliyyat geri alınmır.
              </p>
            </div>
            <div className="flex gap-2 border-t border-brand-line px-5 py-4">
              <button
                onClick={doRefund}
                disabled={busy}
                data-testid="refund-confirm"
                className="flex-1 bg-destructive text-white py-3 text-sm rounded-md disabled:opacity-50"
              >
                {busy ? "Göndərilir..." : "Bəli, pulu qaytar"}
              </button>
              <button
                onClick={() => setRefund(null)}
                disabled={busy}
                className="flex-1 border border-brand-line py-3 text-sm rounded-md disabled:opacity-50"
              >
                İmtina
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2-ci addım: malın taleyi. Çatdırıla bilməyən mal satışa qayıdır,
          zədələnmiş mal isə yox — ona görə soruşulur, avtomatik edilmir. */}
      {askStock && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
             data-testid="restore-modal">
          <div className="bg-white w-full max-w-md rounded-md">
            <div className="flex items-center gap-3 border-b border-brand-line px-5 py-4">
              <Package size={20} strokeWidth={1.5} />
              <p className="font-medium">Məhsul satışa qaytarılsın?</p>
            </div>
            <div className="p-5 text-sm space-y-3">
              <div className="space-y-1">
                {(askStock.order.items || []).map((it, i) => (
                  <p key={i} className="text-brand-sub">• {it.name} ×{it.quantity}</p>
                ))}
              </div>
              <p className="text-xs text-brand-muted">
                <b>Bəli</b> — məhsul çatdırıla bilməyib, yenidən satıla bilər.<br />
                <b>Xeyr</b> — məhsul itib və ya zədələnib, satışa qaytarılmır.
              </p>
            </div>
            <div className="flex gap-2 border-t border-brand-line px-5 py-4">
              <button
                onClick={restoreStock}
                disabled={busy}
                data-testid="restore-confirm"
                className="flex-1 bg-brand-ink text-white py-3 text-sm rounded-md disabled:opacity-50"
              >
                {busy ? "Gözləyin..." : "Bəli, satışa qaytar"}
              </button>
              <button
                onClick={() => setAskStock(null)}
                disabled={busy}
                data-testid="restore-decline"
                className="flex-1 border border-brand-line py-3 text-sm rounded-md disabled:opacity-50"
              >
                Xeyr
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
